import React, { useContext, useEffect, useRef, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { FirebaseContext } from "../contexts/FirebaseContext";
import TopBar from "../components/TopBar";
import LabelFilterStrip from "../components/LabelFilterStrip";
import { useManageLabels } from "../hooks/useManageLabels";
import { ReactComponent as BackIcon } from "../assets/icon_back.svg";
import Colors from "../constants/Colors";
import {
  expandTitleTemplate,
  hasTitleTokens,
  TITLE_TEMPLATE_HELP_LINES,
} from "../utils/titleTemplate";
import {
  RECURRENCE_TYPES,
  TASK_STATE_BACKLOG,
  TASK_STATE_WIP,
  recurrenceLabel,
  weekdayName,
  nextFireAfter,
  CAL_SUNDAY,
  CAL_SATURDAY,
} from "../utils/recurrence";

const DANGER = "#e5484d";

function RuleDetailScreen() {
  const { ruleId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    recurringRules,
    projects,
    labels,
    updateRecurringRule,
    deleteRecurringRule,
  } = useContext(FirebaseContext);

  const rule =
    recurringRules.find((r) => String(r.id) === String(ruleId)) ||
    location.state?.rule ||
    null;

  const scopeProjectId =
    rule && rule.projectId != null ? Number(rule.projectId) : null;
  const openManageLabels = useManageLabels(scopeProjectId);

  const [draft, setDraft] = useState(rule ? { ...rule } : null);
  const [showTitleHelp, setShowTitleHelp] = useState(false);
  useEffect(() => {
    if (!draft && rule) setDraft({ ...rule });
  }, [rule, draft]);

  // Autosave: every edit marks the draft dirty; a 3s debounce persists it (each
  // new edit resets the timer). No Save button — leaving the screen flushes any
  // pending write. draftRef keeps the latest draft for the unmount flush.
  const dirtyRef = useRef(false);
  // True while a persist() write is in flight. Guards the re-seed effect so a
  // remote snapshot (or our own write echo) can't overwrite the draft while we
  // are saving it — otherwise a remote doc carrying stale fields with a newer
  // updatedAt would visibly restore edits the user just removed.
  const inFlightRef = useRef(false);
  // Monotonic edit counter: the autosave only clears the dirty flag if no
  // newer edit landed while its write was in flight.
  const editSeqRef = useRef(0);
  const draftRef = useRef(draft);
  draftRef.current = draft;

  // Reactive: when the live rule changes (e.g. edited on Android), re-seed the
  // editor — but only when the user is truly idle: not mid-edit (dirty) and
  // not mid-save (in flight), so local edits aren't lost.
  // Keyed on updatedAt so it fires on real remote changes, not every render.
  useEffect(() => {
    if (rule && !dirtyRef.current && !inFlightRef.current) setDraft({ ...rule });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rule && rule.updatedAt]);

  const set = (patch) => {
    dirtyRef.current = true;
    editSeqRef.current++;
    setDraft((d) => ({ ...d, ...patch }));
  };

  const persist = async (merged) => {
    if (!merged) return;
    const fields = {
      title: merged.title,
      projectId: Number(merged.projectId),
      recurrenceType: merged.recurrenceType,
      dayOfMonth: merged.dayOfMonth,
      dayOfWeek: merged.dayOfWeek,
      hour: merged.hour,
      minute: merged.minute,
      targetState: merged.targetState,
      setDueDate: !!merged.setDueDate,
      labelIds: (merged.labelIds || []).map(Number),
      enabled: merged.enabled,
      nextFireAt: merged.enabled
        ? nextFireAfter(merged.recurrenceType, merged.dayOfWeek, merged.dayOfMonth, merged.hour, merged.minute, Date.now())
        : merged.nextFireAt || 0,
    };
    await updateRecurringRule(merged.firestoreId || merged.id, fields);
  };

  // Debounced autosave — re-armed on every draft change. The dirty flag is
  // cleared only after the write resolves (and only if no newer edit landed
  // meanwhile), so a failed or in-flight save can't let the re-seed effect
  // discard unsaved edits.
  useEffect(() => {
    if (!draft || !dirtyRef.current) return;
    const t = setTimeout(async () => {
      const seq = editSeqRef.current;
      inFlightRef.current = true;
      try {
        await persist(draft);
        if (editSeqRef.current === seq) dirtyRef.current = false;
      } catch (err) {
        console.error("[rule-detail] autosave failed:", err);
      } finally {
        inFlightRef.current = false;
      }
    }, 3000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft]);

  // Flush any pending edit when leaving the screen.
  useEffect(() => {
    return () => {
      if (dirtyRef.current && draftRef.current) persist(draftRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleLabel = (id) => {
    const ids = (draft.labelIds || []).map(Number);
    const next = ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id];
    set({ labelIds: next });
  };

  const onToggle = async () => {
    const merged = { ...draft, enabled: !draft.enabled };
    setDraft(merged);
    dirtyRef.current = false; // we persist it right here
    inFlightRef.current = true;
    try {
      await persist(merged);
    } finally {
      inFlightRef.current = false;
    }
  };

  const onDelete = async () => {
    dirtyRef.current = false; // don't resurrect a deleted rule on unmount flush
    await deleteRecurringRule(draft);
    navigate(-1);
  };

  const s = styles;

  return (
    <div style={s.container}>
      <TopBar
        leftContainer={
          <button onClick={() => navigate(-1)} style={s.backBtn} aria-label="Back">
            <BackIcon style={{ width: 22, height: 22, fill: "var(--text)" }} />
          </button>
        }
        centerContainer={
          <span style={s.headerTitle}>
            {draft && draft.enabled ? "Active rule" : "Paused rule"}
          </span>
        }
      />

      {!draft ? (
        <div style={s.body}>
          <div style={{ color: "var(--text-muted)" }}>Loading rule…</div>
        </div>
      ) : (
        <div style={s.body}>
          <div style={s.labelRow}>
            <label style={{ ...s.label, margin: 0 }}>Task title</label>
            <button
              type="button"
              style={s.helpBtn}
              onClick={() => setShowTitleHelp((v) => !v)}
              aria-label="About date tokens in the title"
              title="About date tokens"
            >
              ?
            </button>
          </div>
          {showTitleHelp && (
            <div style={s.helpPanel}>
              {TITLE_TEMPLATE_HELP_LINES.map((line, i) => (
                <div key={i} style={i === 0 ? s.helpLead : s.helpLine}>
                  {line}
                </div>
              ))}
            </div>
          )}
          <input
            style={s.input}
            value={draft.title}
            onChange={(e) => set({ title: e.target.value })}
            placeholder="e.g. Review inbox  ·  Tasks $YYYY-$MMM-$DD"
          />
          {hasTitleTokens(draft.title) && (
            <div style={s.preview}>
              Today this would create:{" "}
              <strong>{expandTitleTemplate(draft.title, Date.now())}</strong>
            </div>
          )}

          <label style={s.label}>Project</label>
          <select
            style={s.input}
            value={String(draft.projectId)}
            onChange={(e) => set({ projectId: Number(e.target.value) })}
          >
            {projects.map((p) => (
              <option key={p.id} value={Number(p.id)}>
                {p.projectName}
              </option>
            ))}
          </select>

          {/* Labels — assigned to every task this rule spawns. If a label has a
              default action template, the materialized task also gets its
              actions. "Edit Labels" opens the shared management dialog. */}
          <label style={s.label}>Labels</label>
          <LabelFilterStrip
            labels={labels.filter(
              (l) => l.projectId == null || Number(l.projectId) === Number(draft.projectId)
            )}
            selectedIds={new Set((draft.labelIds || []).map(Number))}
            onToggle={toggleLabel}
            onClearAll={() => {}}
            showAllChip={false}
            onAddLabel={openManageLabels}
          />

          <label style={s.label}>Repeat</label>
          <div style={s.chipRow}>
            {RECURRENCE_TYPES.map((t) => (
              <button
                key={t}
                style={draft.recurrenceType === t ? s.chipOn : s.chip}
                onClick={() => set({ recurrenceType: t })}
              >
                {recurrenceLabel(t)}
              </button>
            ))}
          </div>

          {draft.recurrenceType === "WEEKLY" && (
            <>
              <label style={s.label}>Weekday</label>
              <select
                style={s.input}
                value={draft.dayOfWeek}
                onChange={(e) => set({ dayOfWeek: Number(e.target.value) })}
              >
                {Array.from({ length: CAL_SATURDAY - CAL_SUNDAY + 1 }, (_, i) => CAL_SUNDAY + i).map((d) => (
                  <option key={d} value={d}>
                    {weekdayName(d)}
                  </option>
                ))}
              </select>
            </>
          )}

          {draft.recurrenceType === "MONTHLY" && (
            <Stepper label="Day of month" value={draft.dayOfMonth} min={1} max={28} onChange={(v) => set({ dayOfMonth: v })} />
          )}

          <Stepper label="Hour" value={draft.hour} min={0} max={23} onChange={(v) => set({ hour: v })} />
          <Stepper label="Minute" value={draft.minute} min={0} max={59} onChange={(v) => set({ minute: v })} />

          <label style={s.label}>Create as</label>
          <div style={s.chipRow}>
            <button
              style={draft.targetState === TASK_STATE_BACKLOG ? s.chipOn : s.chip}
              onClick={() => set({ targetState: TASK_STATE_BACKLOG })}
            >
              To-Do
            </button>
            <button
              style={draft.targetState === TASK_STATE_WIP ? s.chipOn : s.chip}
              onClick={() => set({ targetState: TASK_STATE_WIP })}
            >
              WIP
            </button>
          </div>

          <div style={s.toggleRow}>
            <span style={{ flex: 1 }}>Set due date (same day)</span>
            <button
              style={draft.setDueDate ? s.chipOn : s.chip}
              onClick={() => set({ setDueDate: !draft.setDueDate })}
            >
              {draft.setDueDate ? "On" : "Off"}
            </button>
          </div>

          <div style={s.autosaveHint}>Changes save automatically</div>
          <button style={s.primaryBtn} onClick={onToggle}>
            {draft.enabled ? "Pause" : "Activate"}
          </button>
          <button style={s.deleteBtn} onClick={onDelete}>
            Delete rule
          </button>
        </div>
      )}
    </div>
  );
}

function Stepper({ label, value, min, max, onChange }) {
  // `draft` holds the in-progress typed text (null = not editing, show value).
  // Lets the user clear the field and type freely; we clamp to [min,max] on
  // commit (blur / Enter) so only sensible values are persisted.
  const [draft, setDraft] = useState(null);

  const commit = (raw) => {
    const n = parseInt(raw, 10);
    if (!Number.isNaN(n)) onChange(Math.max(min, Math.min(max, n)));
    setDraft(null); // revert to the (clamped) value display
  };

  return (
    <div style={styles.stepperRow}>
      <span style={{ flex: 1 }}>{label}</span>
      <button style={styles.stepBtn} onClick={() => value > min && onChange(value - 1)}>–</button>
      <input
        style={styles.stepInput}
        type="text"
        inputMode="numeric"
        value={draft != null ? draft : String(value).padStart(2, "0")}
        onFocus={(e) => { setDraft(String(value)); e.target.select(); }}
        onChange={(e) =>
          setDraft(e.target.value.replace(/[^0-9]/g, "").slice(0, String(max).length))
        }
        onBlur={(e) => commit(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
        aria-label={label}
      />
      <button style={styles.stepBtn} onClick={() => value < max && onChange(value + 1)}>+</button>
    </div>
  );
}

const styles = {
  container: {
    paddingTop: "50px",
    height: "100vh",
    boxSizing: "border-box",
    background: "var(--bg)",
    color: "var(--text)",
  },
  backBtn: { background: "none", border: "none", cursor: "pointer", display: "flex", alignItems: "center", padding: 0 },
  headerTitle: { fontWeight: 700 },
  body: {
    padding: "16px",
    maxWidth: "560px",
    margin: "0 auto",
    height: "calc(100vh - 50px)",
    overflowY: "auto",
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
    paddingBottom: "40px",
  },
  label: { fontSize: "12px", color: "var(--text-muted)", margin: "12px 0 4px" },
  labelRow: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    margin: "12px 0 4px",
  },
  helpBtn: {
    width: "18px",
    height: "18px",
    lineHeight: "18px",
    padding: 0,
    borderRadius: "50%",
    border: "1px solid var(--border)",
    background: "var(--surface)",
    color: "var(--text-muted)",
    fontSize: "12px",
    fontWeight: 700,
    cursor: "pointer",
    textAlign: "center",
  },
  helpPanel: {
    background: "var(--surface)",
    border: "1px solid var(--border)",
    borderRadius: "8px",
    padding: "10px 12px",
    marginBottom: "8px",
    fontSize: "12.5px",
    color: "var(--text-muted)",
    lineHeight: 1.6,
  },
  helpLead: { color: "var(--text)", marginBottom: "4px" },
  helpLine: { fontFamily: "monospace" },
  preview: {
    marginTop: "6px",
    fontSize: "12.5px",
    color: "var(--text-muted)",
  },
  input: {
    padding: "10px",
    borderRadius: "8px",
    border: "1px solid var(--border)",
    background: "var(--surface)",
    color: "var(--text)",
    fontSize: "14px",
    width: "100%",
    boxSizing: "border-box",
  },
  chipRow: { display: "flex", gap: "8px", flexWrap: "wrap" },
  chip: {
    padding: "8px 16px",
    borderRadius: "16px",
    border: "1px solid var(--border)",
    background: "transparent",
    color: "var(--text)",
    cursor: "pointer",
    fontSize: "13px",
  },
  chipOn: {
    padding: "8px 16px",
    borderRadius: "16px",
    border: `1px solid ${Colors.primary}`,
    background: Colors.primary,
    color: "#fff",
    cursor: "pointer",
    fontSize: "13px",
  },
  toggleRow: { display: "flex", alignItems: "center", gap: "10px", padding: "14px 0 0" },
  stepperRow: { display: "flex", alignItems: "center", gap: "10px", padding: "8px 0" },
  stepBtn: {
    width: "36px",
    height: "36px",
    borderRadius: "8px",
    border: "1px solid var(--border)",
    background: "transparent",
    color: Colors.primary,
    fontSize: "18px",
    cursor: "pointer",
  },
  stepVal: { minWidth: "30px", textAlign: "center", fontWeight: 600 },
  stepInput: {
    width: "44px",
    height: "36px",
    textAlign: "center",
    fontWeight: 600,
    fontSize: "15px",
    color: "var(--text)",
    background: "transparent",
    border: "1px solid var(--border)",
    borderRadius: "8px",
    outline: "none",
    boxSizing: "border-box",
  },
  autosaveHint: {
    marginTop: "20px",
    fontSize: "12px",
    color: "var(--text-muted)",
    textAlign: "center",
  },
  primaryBtn: {
    marginTop: "8px",
    padding: "14px",
    borderRadius: "8px",
    border: "none",
    background: Colors.primary,
    color: "#fff",
    fontSize: "15px",
    cursor: "pointer",
  },
  deleteBtn: {
    marginTop: "10px",
    padding: "14px",
    borderRadius: "8px",
    border: "none",
    background: DANGER,
    color: "#fff",
    fontSize: "15px",
    cursor: "pointer",
  },
};

export default RuleDetailScreen;
